import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlagImage, parseCountry } from 'react-international-phone';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

const Chevron = styled.svg<{ $open: boolean }>`
  transition: transform 150ms ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 50;
  width: 260px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  color: #1e293b;

  &:focus {
    background: #f8fafc;
  }
`;

const OptionList = styled.div`
  max-height: 260px;
  overflow-y: auto;
`;

const OptionRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#f1f5f9' : 'transparent')};

  &:hover {
    background: #f1f5f9;
  }
`;

const CountryName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DialCode = styled.span`
  font-size: 13px;
  color: #64748b;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  padding: 16px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
`;

interface CountryDropdownProps {
  selectedCountry: string;
  countries: unknown[];
  onSelect: (iso2: string) => void;
}

// Replaces react-international-phone's own CountrySelector, which ships
// with its own dated default CSS (plain list rows, native title-attribute
// tooltips on hover). This keeps their country-data/parsing utilities but
// renders the actual dropdown to match the rest of the app.
export const CountryDropdown: React.FC<CountryDropdownProps> = ({ selectedCountry, countries, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      const timer = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const parsed = useMemo(() => countries.map((c) => parseCountry(c as never)), [countries]);
  const current = parsed.find((c) => c.iso2 === selectedCountry);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return parsed;
    return parsed.filter((c) => c.name.toLowerCase().includes(term) || c.dialCode.includes(term));
  }, [parsed, search]);

  return (
    <Wrapper ref={containerRef}>
      <TriggerButton type="button" onClick={() => setIsOpen((v) => !v)}>
        {current && <FlagImage iso2={current.iso2} size="20px" />}
        <Chevron $open={isOpen} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth={2}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </Chevron>
      </TriggerButton>
      {isOpen && (
        <Panel>
          <SearchInput
            ref={searchRef}
            placeholder="Search country or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <OptionList>
            {filtered.length === 0 && <EmptyState>No matches</EmptyState>}
            {filtered.map((c) => (
              <OptionRow
                key={c.iso2}
                $active={c.iso2 === selectedCountry}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(c.iso2);
                  setIsOpen(false);
                }}
              >
                <FlagImage iso2={c.iso2} size="20px" />
                <CountryName>{c.name}</CountryName>
                <DialCode>+{c.dialCode}</DialCode>
              </OptionRow>
            ))}
          </OptionList>
        </Panel>
      )}
    </Wrapper>
  );
};
